/*
 * Presentation Templates — the Asset Library's "Presentation Templates" tab
 * content. Deliberately standalone: this whole feature (this component
 * included) never imports from slide-templates — see this repo's
 * presentation-templates/services/presentationTemplatesApi.ts module header
 * for why. Slide Templates' SlideTemplatesPanel is read only as a reference
 * for the visual/architectural pattern; every helper below (wireframe
 * drawing, card shells) is this component's own copy.
 *
 * TWO RENDER MODES
 * Grid mode (default): one card per presentation — a wireframe drawn from its
 * FIRST slide's placeholder geometry (the presentation's own representative
 * preview), its name, and "{N} slides · click to preview". Clicking a card is
 * NAVIGATION (openPresentation), never a selection toggle — a presentation
 * itself isn't insertable, only its individual slides are.
 *
 * Preview mode (a presentation is open): a "Back" button returns to the grid,
 * a header shows the presentation's name + slide count, and a grid of
 * per-slide cards ("Slide 1", "Slide 2", …) each toggle selection on click.
 *
 * The footer's "Insert into deck" (driven by the shared AssetLibraryContent
 * footer via the imperative `insertSelected` handle + `onSelectionStateChange`
 * callback, same pattern SlideTemplatesPanel uses) always acts on every
 * selected slide across every presentation, not just the one being previewed
 * — selection is a flat set of globally-unique slide ids.
 */

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { usePresentationTemplateThumbnail } from "../hooks/usePresentationTemplateThumbnail";
import { usePresentationTemplates, type UsePresentationTemplatesOptions } from "../hooks/usePresentationTemplates";
import type {
  PresentationSlidePreview,
  PresentationSlidePreviewRole,
  PresentationTemplate,
  PresentationTemplateSlide,
} from "../services/presentationTemplatesApi";
import type { ExtractedSlideImage } from "../../shared/services/pptxSlideImageExtraction";
import styles from "./PresentationTemplatesPanel.module.css";

export interface PresentationTemplatesSelectionState {
  readonly count: number;
  readonly canInsert: boolean;
  readonly isInserting: boolean;
}

export interface PresentationTemplatesPanelHandle {
  insertSelected: () => Promise<void>;
}

interface PresentationTemplatesPanelProps {
  readonly onSelectionStateChange?: (state: PresentationTemplatesSelectionState) => void;
  /**
   * Where the manifest comes from and how a selection is committed — see
   * hooks/usePresentationTemplates.ts. Omit it for the in-pane Asset Library
   * (fetch + insert locally); the Office Dialog window passes the
   * hand-off/messageParent pair instead, since it has neither a bearer token
   * nor PowerPoint.run().
   */
  readonly templatesOptions?: UsePresentationTemplatesOptions;
}

const PREVIEW_ONLY_HINT = "This slide can be previewed but not inserted yet";

function matchesSearch(presentation: PresentationTemplate, query: string): boolean {
  if (!query) return true;
  const haystack = `${presentation.name} ${presentation.category ?? ""}`.toLowerCase();
  return haystack.includes(query.toLowerCase());
}

/** Per-role class, so the wireframe reads as a layout and not a grey mosaic. */
const ROLE_CLASS: Readonly<Record<PresentationSlidePreviewRole, string | undefined>> = {
  title: styles.blockTitle,
  body: styles.blockBody,
  image: styles.blockImage,
  chart: styles.blockChart,
  table: styles.blockTable,
  other: styles.blockOther,
};

/**
 * The placeholder rectangles, drawn as a wireframe.
 *
 * The viewBox is a flat 0–100 square with `preserveAspectRatio="none"`: blocks
 * are already percentages of their own axis, so letting each axis stretch
 * independently to the (correctly-proportioned) box is what puts them in the
 * right place. Only the 1-unit corner radius distorts, which is invisible at
 * thumbnail size.
 */
function LayoutWireframe({ preview }: Readonly<{ preview: PresentationSlidePreview }>) {
  return (
    <svg className={styles.wireframe} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
      {preview.blocks.map((block, index) => (
        <rect
          // Blocks have no ids of their own; the rect's own geometry is what
          // makes it unique within a card (identical ones are deduped upstream).
          key={`${block.role}-${block.x}-${block.y}-${block.w}-${block.h}-${index}`}
          className={ROLE_CLASS[block.role] ?? ROLE_CLASS.other}
          x={block.x}
          y={block.y}
          width={block.w}
          height={block.h}
          rx={1}
        />
      ))}
    </svg>
  );
}

/**
 * Manifest thumbnail, wireframe, or a bare document-icon placeholder — in
 * that priority order. `hasPhoto` suppresses the icon once a real photo is
 * about to be layered on top: a card with nothing left to draw behind a photo
 * that already fills it shouldn't show one.
 */
function ThumbnailBaseLayer({
  thumbnailUrl,
  preview,
  hasPhoto,
}: Readonly<{ thumbnailUrl: string | null; preview: PresentationSlidePreview | null; hasPhoto: boolean }>) {
  if (thumbnailUrl) {
    return <img className={styles.thumbnailImg} src={thumbnailUrl} alt="" loading="lazy" />;
  }
  if (preview) {
    return <LayoutWireframe preview={preview} />;
  }
  if (hasPhoto) {
    return null;
  }
  return (
    <div className={styles.thumbnailFallback} aria-hidden="true">
      <span>🗎</span>
    </div>
  );
}

/**
 * Base layer (manifest thumbnail, wireframe, or a bare placeholder) plus, once
 * the card scrolls into view, the slide's own real embedded photo(s) laid on
 * top — see hooks/usePresentationTemplateThumbnail.ts for why this is lazy
 * and ../shared/services/pptxSlideImageExtraction.ts for why it isn't sourced
 * from the manifest.
 */
function SlideThumbnail({
  slide,
  loadThumbnail,
}: Readonly<{
  slide: PresentationTemplateSlide;
  loadThumbnail?: (slide: PresentationTemplateSlide) => Promise<readonly ExtractedSlideImage[]>;
}>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const images = usePresentationTemplateThumbnail(slide, rootRef, loadThumbnail);

  // Once a card has its real photo(s), the wireframe's "image"-role rects are
  // just a stale placeholder for the same region — drawing both reads as a
  // jumbled mix of real content and abstract blocks, not a clean preview.
  const wireframePreview = useMemo(() => {
    if (images.length === 0 || !slide.preview) return slide.preview;
    const blocks = slide.preview.blocks.filter((block) => block.role !== "image");
    return blocks.length > 0 ? { ...slide.preview, blocks } : null;
  }, [images.length, slide.preview]);

  return (
    <div ref={rootRef} className={styles.thumbnailStack}>
      <ThumbnailBaseLayer thumbnailUrl={slide.thumbnailUrl} preview={wireframePreview} hasPhoto={images.length > 0} />
      {images.map((image, index) => (
        <img
          // Extracted fresh per render from the slide's own picture list —
          // there's no stable id, and position + source together are unique.
          key={`${image.xPct}-${image.yPct}-${index}`}
          className={styles.thumbnailPhoto}
          src={image.dataUrl}
          alt=""
          style={{ left: `${image.xPct}%`, top: `${image.yPct}%`, width: `${image.wPct}%`, height: `${image.hPct}%` }}
        />
      ))}
    </div>
  );
}

/**
 * `onOpen` (navigate into the slide grid) and `onInsertAll` (insert every
 * insertable slide of this presentation directly, no navigation) are two
 * independent actions on the same card. A `<button>` can't contain another
 * interactive `<button>`, so "insert all" lives as a sibling `<button>`,
 * layered over the open button's corner via `.cardWrap`/`.insertAllBtn`'s
 * absolute positioning, rather than nested inside it — both stay real,
 * natively keyboard- and screen-reader-operable buttons this way.
 */
function PresentationCard({
  presentation,
  onOpen,
  onInsertAll,
  insertAllDisabled,
}: Readonly<{
  presentation: PresentationTemplate;
  onOpen: (id: string) => void;
  onInsertAll: (id: string) => void;
  insertAllDisabled: boolean;
}>) {
  const firstSlide = presentation.slides[0] ?? null;
  const slideCountLabel = presentation.slides.length === 1 ? "1 slide" : `${presentation.slides.length} slides`;

  return (
    <div className={styles.cardWrap}>
      <button type="button" className={styles.card} title={presentation.name} onClick={() => onOpen(presentation.id)}>
        <div className={styles.thumbnail}>
          <div className={styles.thumbnailInner}>
            <ThumbnailBaseLayer thumbnailUrl={presentation.thumbnailUrl} preview={firstSlide?.preview ?? null} hasPhoto={false} />
          </div>
          <span className={styles.cardPreviewIcon} aria-hidden="true">
            👁
          </span>
        </div>
        <div className={`${styles.cardFooter} ${styles.cardFooterInset}`}>
          <span className={styles.cardName}>{presentation.name}</span>
          <span className={styles.cardSubtext}>{slideCountLabel}</span>
        </div>
      </button>
      <button
        type="button"
        className={styles.insertAllBtn}
        title="Insert all slides"
        aria-label={`Insert all slides from ${presentation.name}`}
        disabled={insertAllDisabled}
        onClick={() => onInsertAll(presentation.id)}
      >
        ⬇
      </button>
    </div>
  );
}

function SlideCard({
  slide,
  slideIndex,
  selected,
  showPreviewOnlyBadge,
  onToggle,
  loadThumbnail,
}: Readonly<{
  slide: PresentationTemplateSlide;
  slideIndex: number;
  selected: boolean;
  showPreviewOnlyBadge: boolean;
  onToggle: (id: string) => void;
  loadThumbnail?: (slide: PresentationTemplateSlide) => Promise<readonly ExtractedSlideImage[]>;
}>) {
  const toggle = () => onToggle(slide.id);

  return (
    <button
      type="button"
      className={selected ? `${styles.card} ${styles.cardSelected}` : styles.card}
      aria-pressed={selected}
      title={slide.description ?? slide.name}
      onClick={toggle}
    >
      <div className={styles.thumbnail}>
        <div className={styles.thumbnailInner}>
          <SlideThumbnail slide={slide} loadThumbnail={loadThumbnail} />
        </div>
        {selected && (
          <span className={styles.selectedTick} aria-hidden="true">
            ✓
          </span>
        )}
        {showPreviewOnlyBadge && (
          <span className={styles.previewOnlyBadge} title={PREVIEW_ONLY_HINT}>
            Preview only
          </span>
        )}
      </div>
      <div className={styles.cardFooter}>
        <span className={styles.cardName}>{`Slide ${slideIndex}`}</span>
      </div>
    </button>
  );
}

export const PresentationTemplatesPanel = forwardRef<PresentationTemplatesPanelHandle, PresentationTemplatesPanelProps>(
  function PresentationTemplatesPanel({ onSelectionStateChange, templatesOptions }, ref) {
    const {
      loadState,
      insertStatus,
      presentations,
      activePresentation,
      openPresentation,
      closePresentation,
      selectedIds,
      selectedCount,
      toggleSelected,
      retry,
      insertSelected,
      insertAllSlidesOf,
      isInserting,
      insertError,
    } = usePresentationTemplates(templatesOptions);
    const loadThumbnail = templatesOptions?.loadThumbnail;
    const [search, setSearch] = useState("");

    const visiblePresentations = useMemo(
      () => presentations.filter((presentation) => matchesSearch(presentation, search)),
      [presentations, search],
    );

    const presentationCountLabel = presentations.length === 1 ? "presentation" : "presentations";
    const resultCountText =
      visiblePresentations.length === presentations.length
        ? `${presentations.length} ${presentationCountLabel}`
        : `${visiblePresentations.length} of ${presentations.length}`;

    // Every card badged "Preview only" says nothing; one notice above the
    // grid does. Only badge cards when the open presentation's own slide set
    // is actually mixed.
    const noneInsertable = useMemo(
      () =>
        activePresentation !== null &&
        activePresentation.slides.length > 0 &&
        activePresentation.slides.every((slide) => !slide.insertable),
      [activePresentation],
    );

    useImperativeHandle(ref, () => ({ insertSelected }), [insertSelected]);

    useEffect(() => {
      onSelectionStateChange?.({ count: selectedCount, canInsert: selectedCount > 0 && !isInserting, isInserting });
    }, [onSelectionStateChange, selectedCount, isInserting]);

    if (loadState.status === "loading") {
      return (
        <div className={styles.root}>
          <div className={styles.centered}>
            <span className={styles.spinner} aria-hidden="true" />
            <span>Loading presentation templates…</span>
          </div>
        </div>
      );
    }

    if (loadState.status === "error") {
      return (
        <div className={styles.root}>
          <div className={styles.centered}>
            <span className={styles.errorText}>{loadState.message}</span>
            <button type="button" className={styles.retryBtn} onClick={retry}>
              Retry
            </button>
          </div>
        </div>
      );
    }

    let gridContent: ReactNode;
    if (activePresentation === null) {
      gridContent =
        visiblePresentations.length === 0 ? (
          <div className={styles.centered}>
            <span>
              {presentations.length === 0
                ? "No presentation templates are available yet."
                : "No presentations match your search."}
            </span>
          </div>
        ) : (
          <div className={styles.grid}>
            {visiblePresentations.map((presentation) => (
              <PresentationCard
                key={presentation.id}
                presentation={presentation}
                onOpen={openPresentation}
                onInsertAll={insertAllSlidesOf}
                insertAllDisabled={isInserting}
              />
            ))}
          </div>
        );
    } else if (activePresentation.slides.length === 0) {
      gridContent = (
        <div className={styles.centered}>
          <span>This presentation has no slides.</span>
        </div>
      );
    } else {
      gridContent = (
        <div className={styles.grid}>
          {activePresentation.slides.map((slide, index) => (
            <SlideCard
              key={slide.id}
              slide={slide}
              slideIndex={index + 1}
              selected={selectedIds.has(slide.id)}
              showPreviewOnlyBadge={!slide.insertable && !noneInsertable}
              onToggle={toggleSelected}
              loadThumbnail={loadThumbnail}
            />
          ))}
        </div>
      );
    }

    return (
      <div className={styles.root}>
        {activePresentation === null ? (
          <div className={styles.toolbar}>
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Search presentation templates…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search presentation templates"
            />
            <span className={styles.resultCount}>{resultCountText}</span>
          </div>
        ) : (
          <div className={styles.previewHeader}>
            <button type="button" className={styles.backBtn} onClick={closePresentation}>
              ← Back
            </button>
            <span className={styles.previewHeaderName}>{activePresentation.name}</span>
            <span className={styles.resultCount}>
              {activePresentation.slides.length === 1 ? "1 slide" : `${activePresentation.slides.length} slides`}
            </span>
          </div>
        )}

        {noneInsertable && (
          <div className={styles.previewOnlyNotice}>
            These slides can be previewed but not inserted yet — the library doesn&apos;t publish slide content.
          </div>
        )}

        {insertError && <div className={styles.insertError}>{insertError}</div>}
        {/* The dialog window is the only UI in the real host, so it reports the
            insert outcome itself rather than relying on a task-pane toast. */}
        {!insertError && insertStatus && <output className={styles.insertStatus}>{insertStatus}</output>}

        <div className={styles.body}>
          <div className={styles.gridArea}>{gridContent}</div>
        </div>
      </div>
    );
  },
);
